import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Wrench, Camera } from 'lucide-react';
import { API, opts, ApprovalBadge, typeLabel, sourceLabel, BLOCK_REASONS } from './equipmentMeta';
import { EquipmentReviewSheet } from './EquipmentReviewSheet';

const TABS = [
  { key: 'pending', label: 'Pending' }, { key: 'sent_back', label: 'Sent Back' }, { key: 'rejected', label: 'Rejected' },
  { key: 'needs_review', label: 'Historical Review' }, { key: 'inventory', label: 'Inventory Follow-up' },
];

// Approval work on the canonical Equipment/Assets page (Plumbing Profile) — not a second system.
export const EquipmentApprovalPanel = ({ state, setState, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/admin/equipment/approval-counts`, opts).then((r) => setCounts(r.data)).catch(() => {});
    axios.get(`${API}/admin/equipment/queue`, { ...opts, params: { state } }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [state]);
  useEffect(() => { load(); }, [load]);
  const n = (k) => (k === 'inventory' ? (counts.inventory_blocked || 0) + (counts.inventory_reconcile || 0) : counts[k] || 0);
  return (
    <div className="space-y-3" data-testid="equip-approval-panel">
      <div className="flex flex-wrap gap-1.5" data-testid="equip-approval-tabs">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`equip-tab-${t.key}`} onClick={() => setState(t.key)}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${state === t.key ? 'bg-[#D4AF37] border-[#D4AF37] text-black font-semibold' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}>
            {t.label} ({n(t.key)})
          </button>
        ))}
      </div>
      {rows.length === 0 && <p className="text-sm text-[#737373] bg-[#141414] border border-[#262626] rounded-md p-6 text-center" data-testid="equip-queue-empty">Nothing here.</p>}
      {rows.map((a) => (
        <button key={a.id} data-testid={`equip-queue-row-${a.id}`} onClick={() => setOpen(a.id)} className="w-full text-left bg-[#141414] border border-[#262626] rounded-md p-3 hover:border-[#D4AF37] transition-colors">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-white font-medium flex items-center gap-2"><Wrench className="w-4 h-4 text-[#D4AF37]" />{typeLabel(a.type)} <span className="text-[#737373] text-xs">{a.brand} {a.model}</span></p>
            <div className="flex items-center gap-1.5">
              {a.photos?.length > 0 && <span className="text-[10px] text-[#A3A3A3] flex items-center gap-1"><Camera className="w-3 h-3" />{a.photos.length}</span>}
              <ApprovalBadge status={a.approval_status} testid={`equip-queue-status-${a.id}`} />
            </div>
          </div>
          <p className="text-xs text-[#A3A3A3] mt-1">{a.customer_name || 'No customer'} · {a.installed_by || 'Unknown tech'}{a.job?.date ? ` · ${a.job.date}` : ''} · {sourceLabel(a.equipment_source)}{a.serial ? ` · S/N ${a.serial}` : ''}</p>
          {state === 'needs_review' && <p className="text-[11px] text-amber-300 mt-0.5">{(a.reconciliation_reasons || []).map((r) => r.replace(/_/g, ' ')).join(' · ')}</p>}
          {state === 'inventory' && a.consumption && <p className="text-[11px] text-amber-300 mt-0.5">{a.inventory_reconcile_needed ? 'Changed after usage was recorded' : (BLOCK_REASONS[a.consumption.reason] || a.consumption.reason)}</p>}
        </button>
      ))}
      {open && <EquipmentReviewSheet assetId={open} onClose={() => setOpen(null)} onChanged={() => { load(); onChanged && onChanged(); }} />}
    </div>
  );
};

export default EquipmentApprovalPanel;
